import { api } from '@goaltree/backend/convex/_generated/api'
import { SidebarInset } from '@goaltree/ui/components/sidebar'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useQuery } from 'convex/react'
import { ChevronRight, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { AppSidebar } from '@/components/app-sidebar'
import { AvailabilityEditor } from '@/components/coaching/availability-editor'
import {
  CheckInDots,
  ProgressBar,
  StatusPill,
  useLastActive,
} from '@/components/coaching/coach-dashboard'
import { InviteClient } from '@/components/coaching/invite-client'
import { MobileScreenHeader } from '@/components/mobile-screen-header'
import { PersistentSidebarProvider as SidebarProvider } from '@/components/persistent-sidebar-provider'
import { SiteHeader } from '@/components/site-header'
import { UserAvatar } from '@/components/user-avatar'

export const Route = createFileRoute('/_authenticated/coaching/')({
  head: () => ({
    meta: [{ title: 'Coaching — Goaltree' }, { name: 'robots', content: 'noindex, nofollow' }],
  }),
  component: RouteComponent,
})

function RouteComponent() {
  const { t } = useTranslation()
  const clients = useQuery(api.coaching.myClients)
  const lastActive = useLastActive()
  const needsAttention = clients?.filter((c) => c.week.status !== 'ok').length ?? 0

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset className="h-svh overflow-hidden bg-background">
        <MobileScreenHeader title={t('coaching.title')} showInbox={false} />
        <SiteHeader className="hidden lg:flex" />
        <div className="flex-1 overflow-y-auto px-4 pb-16 lg:pb-10">
          <div className="mx-auto max-w-(--page-col) pt-6 lg:pt-10">
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              {t('coaching.thisWeek')}
            </p>
            <h1 className="mt-1 text-2xl font-black tracking-tight">
              {t('coaching.clientsCount', { count: clients?.length ?? 0 })}
            </h1>
            {clients && clients.length > 0 && (
              <p className="mt-1 text-sm text-muted-foreground">
                {t('coaching.summary', {
                  attention: needsAttention,
                  ok: clients.length - needsAttention,
                })}
              </p>
            )}
            <InviteClient />
            <AvailabilityEditor />

            <div className="mt-5 overflow-hidden rounded-2xl border bg-card">
              {clients === undefined ? (
                Array.from({ length: 3 }, (_, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 border-t px-4 py-3 first:border-t-0"
                  >
                    <div className="size-9 animate-pulse rounded-full bg-muted/50" />
                    <div className="h-4 flex-1 animate-pulse rounded bg-muted/40" />
                  </div>
                ))
              ) : clients.length === 0 ? (
                <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
                  <Users className="size-8 text-muted-foreground" strokeWidth={1.75} />
                  <p className="font-semibold">{t('coaching.emptyTitle')}</p>
                  <p className="max-w-sm text-sm text-muted-foreground">
                    {t('coaching.emptyDesc')}
                  </p>
                  <p className="max-w-sm text-xs text-muted-foreground">{t('coaching.emptyAlt')}</p>
                </div>
              ) : (
                clients.map((c) => (
                  <Link
                    key={c.linkId}
                    to="/coaching/$clientId"
                    params={{ clientId: c.clientId }}
                    className="grid grid-cols-[36px_minmax(0,1fr)_auto] items-center gap-3 border-t px-4 py-3 transition-colors first:border-t-0 hover:bg-muted/40 md:grid-cols-[36px_minmax(0,1.4fr)_minmax(0,1fr)_auto_auto_16px]"
                  >
                    <UserAvatar
                      image={c.client.image}
                      name={c.client.name}
                      size={36}
                      className="size-9 rounded-full"
                    />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold">{c.client.name}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {c.week.topGoal
                          ? `${c.week.topGoal.title} · ${Math.round(c.week.topGoal.progress)}%`
                          : t('coaching.noActiveGoal')}
                      </p>
                    </div>
                    <div className="hidden min-w-0 md:block">
                      {c.week.topGoal && <ProgressBar value={c.week.topGoal.progress} />}
                    </div>
                    <div className="hidden flex-col items-end gap-1 md:flex">
                      {c.week.checkIns !== null ? (
                        <>
                          <CheckInDots count={c.week.checkIns} />
                          <span className="font-mono text-[10.5px] text-muted-foreground">
                            {t('coaching.daysOf7', { count: c.week.checkIns })}
                          </span>
                        </>
                      ) : (
                        <span className="text-[11px] text-muted-foreground">
                          {t('coaching.hidden')}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <StatusPill status={c.week.status} />
                      <span className="font-mono text-[10.5px] text-muted-foreground">
                        {lastActive(c.week.lastActiveAt)}
                      </span>
                    </div>
                    <ChevronRight className="hidden size-4 text-muted-foreground md:block" />
                  </Link>
                ))
              )}
            </div>
            <p className="mt-3 text-xs text-muted-foreground">{t('coaching.statusHelp')}</p>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}
